import type { TabConfig } from 'src/components/tabbed-modal';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid, Link, Stack, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { fDate } from 'src/utils/format-time';
import { capitalize } from 'src/utils/change-case';
import { getPriorityColor } from 'src/utils/colors';
import { formatSiteExternalId } from 'src/utils/format-string';

import { useGetTask } from 'src/actions/task';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { TabbedModal } from 'src/components/tabbed-modal';
import { SplashScreen } from 'src/components/loading-screen';
import { EntityAuditTrail } from 'src/components/entity-audit-trail';

import { TaskStatus } from '../../../types/dto/task.dto';

import type { TaskDTO } from '../../../types/dto/task.dto';

// ----------------------------------------------------------------------

interface TaskDetailsModalProps {
  open: boolean;
  onClose: () => void;
  id: number | string;
  tabId?: 'details' | 'audit-trails';
  onTaskLoaded?: (task: TaskDTO) => void;
}

// Example Details Tab Content
function DetailsTabContent({ task }: { task: TaskDTO }) {
  const { t } = useTranslation('tasks');
  const customerId = task?.project?.customer?.id;
  const siteLabel = task?.site
    ? `${task.site.externalId ? `${formatSiteExternalId(task.site.externalId)} - ` : ''}${task.site.name}`
    : null;

  return (
    <Grid container spacing={3} p={1} pt={0}>
      <Grid item xs={12} md={4}>
        <Stack gap={2}>
          <Typography color="grey.500">{t('details.fields.project')}</Typography>
          <Typography color="grey.500">{t('details.fields.site')}</Typography>
          <Typography color="grey.500">{t('details.fields.activity')}</Typography>
          <Typography color="grey.500">{t('details.fields.status')}</Typography>
          <Typography color="grey.500">{t('details.fields.priority')}</Typography>
          <Typography color="grey.500">{t('details.fields.assignee')}</Typography>
          <Typography color="grey.500">{t('details.fields.dueDate')}</Typography>
          <Typography color="grey.500">{t('details.fields.followUpDate')}</Typography>
          <Typography color="grey.500">{t('details.fields.description')}</Typography>
        </Stack>
      </Grid>
      <Grid item xs={12} md={8}>
        <Stack gap={2}>
          <Typography color="text.primary">
            {task?.project?.id ? <Link
              href={paths.dashboard.projects.details(task?.project?.id.toString())}
              target="_blank"
              underline="always"
              color="inherit"
            >
              {`#${task?.project?.id}`}
            </Link> : '—'}
          </Typography>
          <Typography color="text.primary">
            {siteLabel ? (
              customerId ? (
                <Link
                  href={paths.dashboard.client.detailsSitesTab(customerId)}
                  target="_blank"
                  underline="always"
                  color="inherit"
                >
                  {siteLabel}
                </Link>
              ) : (
                siteLabel
              )
            ) : (
              '—'
            )}
          </Typography>
          <Typography color="text.primary">{task?.activity?.id ? `#${task?.activity?.id}` : '—'}</Typography>
          <Typography color="text.primary">
            <Label
              color={task?.status === TaskStatus.OPEN ? 'success' : 'default'}
              variant="outlined"
            >
              {capitalize(task?.status)}
            </Label>
          </Typography>
          <Typography color="text.primary" display="flex" gap={1}>
            {task?.priority ? (
              <>
                <Iconify
                  sx={{ width: 16, height: 16, color: getPriorityColor(task?.priority) }}
                  icon="solar:flag-bold"
                />
                <Typography variant="body2" color="text.primary">
                  {capitalize(task?.priority?.toLowerCase())}
                </Typography>
              </>
            ) : (
              <Typography color="text.primary">—</Typography>
            )}
          </Typography>
          <Typography color="text.primary">{task?.assignedTo?.name ?? '—'}</Typography>
          <Typography color="text.primary">{task?.dueDate ? fDate(task?.dueDate) : '—'}</Typography>
          <Typography color="text.primary">
            {task?.followUpDate ? fDate(task?.followUpDate) : '—'}
          </Typography>
          <div
            dangerouslySetInnerHTML={{ __html: task?.description || '' }}
            style={{
              color: 'text.primary',
              fontSize: '0.875rem',
              lineHeight: 1.5,
            }}
          />
        </Stack>
      </Grid>
    </Grid>
  );
}

export function TaskDetailsModal({
  open,
  onClose,
  id: taskId,
  tabId = 'details',
  onTaskLoaded,
}: TaskDetailsModalProps) {
  const { t } = useTranslation('tasks');
  const { task, taskLoading, mutateTask } = useGetTask(taskId);
  const tabs: TabConfig[] = [
    {
      id: 'details',
      label: t('details.tabs.details'),
      icon: 'solar:list-bold',
      component: task ? <DetailsTabContent task={task} /> : null,
    },
    {
      id: 'audit-trails',
      label: t('details.tabs.activity'),
      icon: 'mdi:chat-bubble-outline',
      component: <EntityAuditTrail entityId={taskId.toString()} entityType="Task" />,
    },
  ];

  useEffect(() => {
    let cancelled = false;

    if (open) {
      void mutateTask()
        .then((latestTask) => {
          if (!cancelled && latestTask) {
            onTaskLoaded?.(latestTask);
          }
        })
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
    };
  }, [open, mutateTask, onTaskLoaded]);

  if (taskLoading) return <SplashScreen />;

  return (
    <TabbedModal
      open={open}
      onClose={onClose}
      title={`${task?.title} #${task?.id}`}
      tabs={tabs}
      defaultTab={tabId}
      maxWidth="sm"
    />
  );
}
